import { Icon } from '#app/components/ui/icon.tsx'
import { cn } from '#app/utils/misc.tsx'
import { ResponsiveMarketingImage } from './ResponsiveMarketingImage'

export interface ScreenshotItem {
	title?: string
	description?: string
	imageUrl: string
	imageAlt?: string
}

export interface TextBlockItem {
	title: string
	description?: string
	icon?: string
}

export interface ScreenshotShowcaseProps {
	eyebrow?: string
	title?: string
	subtitle?: string
	background?: 'default' | 'muted' | 'primary-light'
	featuredImage?: ScreenshotItem
	topRow?: ScreenshotItem[]
	bottomRow?: ScreenshotItem[]
	textBlocks?: TextBlockItem[]
}

const backgroundStyles = {
	default: 'bg-background',
	muted: 'bg-muted/30',
	'primary-light': 'bg-primary/5',
}

export function ScreenshotShowcase({
	eyebrow,
	title,
	subtitle,
	background = 'default',
	featuredImage,
	topRow = [],
	bottomRow = [],
	textBlocks = [],
}: ScreenshotShowcaseProps) {
	return (
		<section
			className={cn(
				'px-6 py-28 sm:px-8 lg:px-12',
				backgroundStyles[background],
			)}
		>
			<div className="mx-auto flex max-w-7xl flex-col gap-10">
				{(eyebrow || title || subtitle) && (
					<header className="mx-auto flex max-w-3xl flex-col gap-4 text-center">
						{eyebrow && (
							<span className="text-label text-muted-foreground">
								{eyebrow}
							</span>
						)}
						{title && <h2 className="text-title-2 mt-4">{title}</h2>}
						{subtitle && (
							<p className="text-muted-foreground mx-auto mt-5 max-w-2xl text-base leading-relaxed md:text-lg">
								{subtitle}
							</p>
						)}
					</header>
				)}

				<div className="flex flex-col gap-10">
					{featuredImage ? (
						<div className="border-border mt-14 overflow-hidden rounded-[24px] border shadow-[0_24px_48px_-12px_var(--landing-shadow-low)]">
							<div>
								<ResponsiveMarketingImage
									src={featuredImage.imageUrl}
									alt={
										featuredImage.imageAlt ||
										featuredImage.title ||
										'Marketing screenshot'
									}
									className="block w-full"
									width={2400}
									height={1500}
									sizes="100vw"
									priority
								/>
							</div>
						</div>
					) : null}

					{topRow.length > 0 && (
						<div className="grid gap-6 md:grid-cols-2">
							{topRow.map((item, index) => (
								<div
									key={`${item.title ?? 'top-row'}-${index}`}
									className="flex flex-col gap-3"
								>
									<div className="border-border bg-card aspect-[16/10] overflow-hidden rounded-lg border shadow-sm">
										<ResponsiveMarketingImage
											src={item.imageUrl}
											alt={
												item.imageAlt || item.title || 'Marketing screenshot'
											}
											className="h-full w-full object-cover"
											width={1600}
											height={1000}
											sizes="(min-width: 768px) 50vw, 100vw"
										/>
									</div>
									<div className="space-y-1">
										<h3 className="text-foreground text-base font-semibold md:text-lg">
											{item.title}
										</h3>
										{item.description && (
											<p className="text-muted-foreground text-sm">
												{item.description}
											</p>
										)}
									</div>
								</div>
							))}
						</div>
					)}

					{bottomRow.length > 0 && (
						<div className="grid gap-6 md:grid-cols-3">
							{bottomRow.map((item, index) => (
								<div
									key={`${item.title ?? 'bottom-row'}-${index}`}
									className="flex flex-col gap-3"
								>
									<div className="border-border bg-card aspect-video overflow-hidden rounded-lg border shadow-sm">
										<ResponsiveMarketingImage
											src={item.imageUrl}
											alt={
												item.imageAlt || item.title || 'Marketing screenshot'
											}
											className="h-full w-full object-cover"
											width={1600}
											height={900}
											sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
										/>
									</div>
									<div className="space-y-1">
										<h3 className="text-foreground text-base font-semibold">
											{item.title}
										</h3>
										{item.description && (
											<p className="text-muted-foreground text-sm">
												{item.description}
											</p>
										)}
									</div>
								</div>
							))}
						</div>
					)}

					{textBlocks.length > 0 && (
						<div className="grid gap-6 md:grid-cols-3">
							{textBlocks.map((item, index) => (
								<div
									key={`${item.title}-${index}`}
									className="border-border bg-card/40 flex h-full flex-col gap-2 rounded-lg border p-5 shadow-sm"
								>
									{item.icon && (
										<span className="bg-primary/10 text-primary flex size-9 items-center justify-center rounded-full">
											<Icon name={item.icon as any} className="size-4" />
										</span>
									)}
									<h3 className="text-foreground text-base font-semibold">
										{item.title}
									</h3>
									{item.description && (
										<p className="text-muted-foreground text-sm">
											{item.description}
										</p>
									)}
								</div>
							))}
						</div>
					)}
				</div>
			</div>
		</section>
	)
}
